import { Zap, Leaf, BrainCircuit, Trophy, ArrowRight } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

const vpIcons = [Zap, Leaf, BrainCircuit, Trophy];

export default function DeepMeta() {
  const [ref, isVisible] = useScrollReveal();
  const [ref2, isVisible2] = useScrollReveal();
  const { t } = useLanguage();
  const d = t.deepmeta;

  return (
    <section id="deepmeta" data-testid="deepmeta-section" ref={ref} className="bg-[#1E3455] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(34,197,94,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.02) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <div className="mb-16 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(24px)" }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-12 bg-emerald-500" />
            <span className="text-emerald-400 text-xs uppercase tracking-widest">{d.label}</span>
          </div>
          <div className="inline-flex items-center gap-2 border border-emerald-500/20 bg-emerald-500/5 px-4 py-2 mb-8">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-300 text-xs uppercase tracking-widest font-medium">{d.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl text-white leading-tight max-w-3xl mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {d.heading}<br />
            <span className="bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">{d.headingAccent}</span>
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl leading-relaxed">{d.subtitle}</p>
        </div>

        {/* Body + Role note */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s" }}>
          <div className="space-y-5">
            <p className="text-slate-300 text-base leading-relaxed">{d.body1}</p>
            <p className="text-slate-300 text-base leading-relaxed">{d.body2}</p>
            <p className="text-slate-300 text-base leading-relaxed">{d.body3}</p>
          </div>
          <div className="flex flex-col justify-center">
            {/* Role card */}
            <div className="border-l-2 border-emerald-500/40 pl-6 py-4 bg-emerald-500/5">
              <p className="text-slate-300 text-sm leading-relaxed italic">{d.roleNote}</p>
            </div>
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <a href="https://www.deepmeta.io" target="_blank" rel="noreferrer" data-testid="deepmeta-cta-primary"
                className="bg-emerald-500 text-[#0B1221] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-emerald-400 transition-colors duration-300 flex items-center justify-center gap-3 group">
                {d.ctaPrimary}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button data-testid="deepmeta-cta-secondary"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="border border-emerald-500/40 text-emerald-400 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-emerald-500 hover:text-[#0B1221] transition-all duration-300">
                {d.ctaSecondary}
              </button>
            </div>
          </div>
        </div>

        {/* Value Points */}
        <div ref={ref2} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800/40">
          {d.valuePoints.map((vp, i) => {
            const Icon = vpIcons[i];
            return (
              <div key={i} data-testid={`deepmeta-vp-${i}`}
                className="bg-[#0F1A2E] p-8 group hover:bg-[#162A45] transition-colors duration-400 relative overflow-hidden"
                style={{ opacity: isVisible2 ? 1 : 0, transform: isVisible2 ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.6s ease-out, transform 0.6s ease-out, background-color 0.4s", transitionDelay: `${i * 100}ms` }}>
                <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center mb-5 group-hover:bg-emerald-500/20 transition-colors duration-300">
                  <Icon size={18} className="text-emerald-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-white text-base font-semibold mb-3">{vp.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed group-hover:text-slate-300 transition-colors duration-300">{vp.desc}</p>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-emerald-500 group-hover:w-full transition-all duration-500" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
